/**
 * ChatRoom — the live room content: pinned strip, scrolling message list,
 * input. Owns the useChat hook for a single room key so switching rooms
 * teardown/reconnects cleanly.
 */
import { useEffect, useRef, useState } from "react";
import { useChat } from "@/hooks/useChat";
import ChatMessage from "@/components/chat/ChatMessage";
import ChatInput from "@/components/chat/ChatInput";
import { api } from "@/lib/api";
import { Pin, Users, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";

export default function ChatRoom({ room }) {
  const { messages, pinned, presence, status } = useChat(room);
  const bottomRef = useRef(null);
  const scrollWrapRef = useRef(null);
  const stickBottomRef = useRef(true);
  const [reporting, setReporting] = useState(null);

  // Autoscroll to bottom on new messages IF the user is already near the
  // bottom. If they scrolled up to read history we don't yank them down.
  useEffect(() => {
    if (!stickBottomRef.current) return;
    bottomRef.current && bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const onScroll = () => {
    const el = scrollWrapRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickBottomRef.current = nearBottom;
  };

  const submitReport = async () => {
    if (!reporting) return;
    try {
      await api.post(`/chat/messages/${reporting.id}/report`, { reason: reporting.reason || "" });
      toast.success("Reported — an admin will review");
    } catch (e) { toast.error(e?.response?.data?.detail || "Report failed"); }
    finally { setReporting(null); }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Room header — presence + connection status */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-[#2A2A2A] bg-[#0A0A0A]">
        <div className="font-display text-sm">#{room}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1">
          <Users className="h-3 w-3" /> {presence} live
        </div>
        <div className="ml-auto font-mono text-[10px] uppercase tracking-widest flex items-center gap-1">
          {status === "open" ? (
            <><Wifi className="h-3 w-3 text-green-500" /><span className="text-green-500">connected</span></>
          ) : (
            <><WifiOff className="h-3 w-3 text-yellow-500" /><span className="text-yellow-500">{status}</span></>
          )}
        </div>
      </div>

      {/* Pinned strip */}
      {pinned && pinned.length > 0 && (
        <div className="border-b border-[#FF4500]/40 bg-[#FF4500]/[0.06] px-4 py-2 space-y-2 max-h-40 overflow-y-auto">
          {pinned.map((m) => (
            <div key={`pin-${m.id}`} data-testid={`pinned-${m.id}`} className="flex items-start gap-2">
              <Pin className="h-3 w-3 mt-1 text-[#FF4500] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-display text-xs text-[#FF4500]">{m.display_name}</div>
                <div className="font-mono text-xs text-gray-200 truncate">{m.text || (m.kind === "track_drop" ? `dropped a track: ${m.track_snapshot?.title || ""}` : "")}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Scrolling messages */}
      <div
        ref={scrollWrapRef}
        onScroll={onScroll}
        data-testid={`chat-scroll-${room}`}
        className="flex-1 overflow-y-auto"
      >
        {messages.length === 0 && (
          <div className="py-16 text-center font-mono text-xs text-gray-500 uppercase tracking-widest">
            No messages yet — be the first to say hi.
          </div>
        )}
        {messages.map((m) => (
          <ChatMessage key={m.id} msg={m} onReport={(x) => setReporting({ id: x.id, reason: "" })} />
        ))}
        <div ref={bottomRef} />
      </div>

      <ChatInput room={room} />

      {/* Report modal — minimal inline */}
      {reporting && (
        <div className="fixed inset-0 bg-black/70 grid place-items-center z-50" onClick={() => setReporting(null)}>
          <div
            className="bg-[#0A0A0A] border border-[#2A2A2A] p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-display text-lg mb-2">Report message</div>
            <div className="font-mono text-[11px] text-gray-500 mb-3">
              Tell us what's wrong so an admin can review. Optional.
            </div>
            <input
              data-testid="chat-report-reason"
              value={reporting.reason}
              onChange={(e) => setReporting({ ...reporting, reason: e.target.value })}
              placeholder="Reason (optional)"
              className="w-full bg-black border border-[#2A2A2A] px-3 h-10 font-mono text-sm mb-3"
              maxLength={200}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setReporting(null)}
                className="h-9 px-4 border border-[#2A2A2A] font-mono text-xs uppercase tracking-widest"
              >Cancel</button>
              <button
                data-testid="chat-report-submit"
                onClick={submitReport}
                className="h-9 px-4 bg-[#FF4500] text-black font-mono text-xs uppercase tracking-widest"
              >Send report</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
